import { EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { SharePage } from './components/SharePage';
import { renderBootFailureFallback, runEntraBoot } from './lib/entraBoot';
import './i18n/config';
import './index.css';

// A client share link (/share/<token>) is a public, view-only page: it renders
// on its own, before and instead of any sign-in, so MSAL is never initialized
// and no redirect to Microsoft login can happen. The app itself (and MSAL,
// telemetry, the global error handlers App installs on load) is imported only
// on the signed-in path.
const shareMatch = /^\/share\/([A-Za-z0-9_-]{43})\/?$/.exec(window.location.pathname);

if (shareMatch) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <SharePage token={shareMatch[1]} />
    </StrictMode>,
  );
} else {
  const [{ default: App }, { ErrorBoundary }, { msalInstance }] = await Promise.all([
    import('./App'),
    import('./components/ErrorBoundary'),
    import('./lib/msalConfig'),
  ]);

  // These two run before anything is rendered, so a rejection used to leave
  // #root empty for good — the "Pantalla en blanco" report (#385). Guarded: the
  // failure is logged once, under its own tag, and says so on screen.
  const booted = await runEntraBoot({
    initialize: () => msalInstance.initialize(),
    handleRedirectPromise: () => msalInstance.handleRedirectPromise(),
    renderFallback: (error) => renderBootFailureFallback(document.getElementById('root'), error),
  });

  // Not booted means the fallback screen is already up; mounting the app on top
  // of it would only put the blank screen back.
  if (booted) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && 'account' in event.payload) {
        const { account } = event.payload as { account: import('@azure/msal-browser').AccountInfo | null };
        if (account) msalInstance.setActiveAccount(account);
      }
    });

    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorBoundary>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  }
}
